import { useEffect, useMemo, useState } from "react";

import { useSearchParams } from "@remix-run/react";
import { Settings, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Textarea } from "~/components/ui/textarea";
import { P } from "~/components/ui/typography";
import type { TPartInteraction } from "~/database/types";
import { usePartMutation } from "~/hooks/use-session";
import { useSessionResearchRelease } from "~/hooks/use-session-research-release";

export function ManagePartDialog({
    part,
    sessionParts,
    sessionId,
    sessionOwnerId,
    isPublicSession,
}: {
    part: TPartInteraction;
    sessionParts: TPartInteraction[];
    sessionId: string;
    sessionOwnerId: string;
    isPublicSession?: boolean;
}) {
    const [open, setOpen] = useState(false);
    const [selectedAction, setSelectedAction] = useState<
        "rename-interaction" | "split" | "merge" | "delete" | null
    >(null);
    const [newName, setNewName] = useState(part?.name);
    const [splitInstructions, setSplitInstructions] = useState("");
    const [mergePartIds, setMergePartIds] = useState<string[]>([]);
    const [shareFeedback, setShareFeedback] = useState(false);

    const {
        sessionHasResearchReleaseEnabled,
        effectiveResearchReleaseEnabled,
        toggleResearchRelease,
        isPending: isResearchReleasePending,
    } = useSessionResearchRelease({ sessionId });

    // Reset states when dialog opens/closes
    useEffect(() => {
        if (open) {
            setNewName(part.name);
            setSplitInstructions("");
            setMergePartIds([]);
            setShareFeedback(false);
        }
    }, [open, part?.name]);

    // Set initial merge state when merge is selected
    useEffect(() => {
        if (selectedAction === "merge") {
            setMergePartIds([]);
        }
    }, [selectedAction, part.id]);

    const [searchParams] = useSearchParams();
    const sessionToken = searchParams.get("sessionToken");
    const { mutate, isPending } = usePartMutation(
        sessionId,
        sessionOwnerId,
        sessionToken,
    );

    // We'll filter out the current part from the other parts for merging
    const otherParts = useMemo(
        () => sessionParts.filter((p) => p.id !== part.id),
        [sessionParts, part.id],
    );

    const pendingMergeParts = useMemo(() => {
        if (selectedAction !== "merge") return [];
        return mergePartIds.map((id) => sessionParts.find((p) => p.id === id));
    }, [selectedAction, mergePartIds, sessionParts]);

    const isDisabled = useMemo(() => {
        if (!selectedAction) return false;
        if (selectedAction === "rename-interaction" && !newName) return true;
        if (selectedAction === "merge" && mergePartIds.length === 0)
            return true;
        return false;
    }, [selectedAction, newName, splitInstructions, mergePartIds]);

    const handleConfirm = () => {
        if (!selectedAction) return;

        // AIDEV-NOTE: Block operations for anonymous/public session users
        if (isPublicSession) {
            toast.error("You can only do this with your own sessions.");
            setOpen(false);
            setSelectedAction(null);
            return;
        }

        if (selectedAction === "rename-interaction") {
            // AIDEV-NOTE: EVO-1697 — gear-menu rename targets this specific
            // interaction. The server also propagates the new name to the
            // canonical part (when linked) so cards/headers/parts list reflect
            // it, but other sessions' interactions keep their original names.
            mutate({
                action: "rename-interaction",
                sessionId,
                partId: part.id,
                newName,
            });
        } else if (selectedAction === "delete") {
            mutate({
                action: "delete",
                sessionId,
                partId: part.id,
            });
        } else if (selectedAction === "split") {
            mutate({
                action: "split",
                sessionId,
                partId: part.id,
                instructions: splitInstructions,
            });
        } else if (selectedAction === "merge") {
            if (mergePartIds.length === 0) {
                toast.error("Select at least one other part to merge with.");
                return;
            }
            mutate({
                action: "merge",
                sessionId,
                mergePartIds: mergePartIds.concat(part.id),
            });
        }
        setOpen(false);
        setSelectedAction(null);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedAction(null);
    };

    // AIDEV-NOTE: Hide manage dialog button entirely for public sessions
    if (isPublicSession) {
        return null;
    }

    return (
        <Dialog
            open={open}
            onOpenChange={handleClose}
            analyticsId={`part-detail-manage-part-dialog`}
        >
            <Button
                variant="ghostIcon"
                className="px-2"
                onClick={() => setOpen(true)}
            >
                Part
                <Settings className="size-5" />
            </Button>

            <DialogContent className="z-[1000] max-h-[calc(100dvh-1rem)]">
                <DialogHeader>
                    <DialogTitle>
                        {!selectedAction
                            ? "Edit Part"
                            : selectedAction === "rename-interaction"
                              ? "Rename Part"
                              : selectedAction === "split"
                                ? "Split Part"
                                : selectedAction === "merge"
                                  ? "Merge Parts"
                                  : "Delete Part"}
                    </DialogTitle>
                    <DialogDescription>
                        {!selectedAction
                            ? "How would you like to edit this part?"
                            : selectedAction === "rename-interaction"
                              ? "Enter a new name for this part"
                              : selectedAction === "split"
                                ? "I'll split this part into two separate parts if you give me a hint at what I should create. This will take 10-20 seconds."
                                : selectedAction === "merge"
                                  ? "Which parts would you like to merge with this part?"
                                  : "Remove this part from the session"}
                    </DialogDescription>
                </DialogHeader>

                {!selectedAction ? (
                    // AIDEV-NOTE: Wrap + center on mobile so the Rename/Split/Merge/Delete
                    // controls don't overflow the narrow dialog; left-align on desktop.
                    <div className="flex flex-row flex-wrap items-center justify-center gap-2 sm:justify-start">
                        <Button
                            variant="default"
                            onClick={() =>
                                setSelectedAction("rename-interaction")
                            }
                            size="lg"
                        >
                            Rename
                        </Button>
                        <Button
                            variant="default"
                            onClick={() => setSelectedAction("split")}
                            size="lg"
                        >
                            Split
                        </Button>
                        <Button
                            variant="default"
                            onClick={() => setSelectedAction("merge")}
                            size="lg"
                        >
                            Merge
                        </Button>
                        {/* AIDEV-NOTE: Labeled like its siblings — icon-only delete was
                            undiscoverable (prod feedback: user asked how to delete a part). */}
                        <Button
                            variant="ghost"
                            onClick={() => setSelectedAction("delete")}
                            size="lg"
                            className="gap-2 text-destructive hover:text-destructive"
                        >
                            <Trash2 className="size-5" />
                            Delete
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* RENAME */}
                        {selectedAction === "rename-interaction" && (
                            <div className="flex flex-col gap-2">
                                <Input
                                    className="text-md"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                />
                            </div>
                        )}

                        {/* SPLIT */}
                        {selectedAction === "split" && (
                            <div className="flex flex-col gap-2">
                                <Textarea
                                    placeholder="Briefly describe the two parts you want to split into"
                                    value={splitInstructions}
                                    onChange={(e) =>
                                        setSplitInstructions(e.target.value)
                                    }
                                />
                            </div>
                        )}

                        {/* MERGE */}
                        {selectedAction === "merge" && (
                            <div className="flex flex-col gap-2">
                                <MergeMultiSelect
                                    otherParts={otherParts}
                                    selectedIds={mergePartIds}
                                    onChange={setMergePartIds}
                                />
                                {mergePartIds.length > 0 ? (
                                    <Card className="text-md p-2 text-brand-terra-600">
                                        Ok, I'll merge "{part.name}" and{" "}
                                        {mergePartIds.length === 0
                                            ? "whatever you select"
                                            : mergePartIds.length === 1
                                              ? `"${pendingMergeParts[0]?.name}".`
                                              : `the ${mergePartIds.length} parts you've selected.`}
                                    </Card>
                                ) : (
                                    <Card className="text-md h-10 p-2 text-brand-terra-600"></Card>
                                )}
                            </div>
                        )}

                        {/* DELETE */}
                        {selectedAction === "delete" && (
                            <div className="rounded-md bg-red-50 p-4 text-sm text-brand-forest-800">
                                Are you sure? This cannot be undone.
                            </div>
                        )}
                        <div className="p-1"></div>

                        <DialogFooter>
                            <Button
                                variant="ghost"
                                onClick={() => setSelectedAction(null)}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="default"
                                onClick={handleConfirm}
                                isLoading={isPending}
                                disabled={isDisabled}
                            >
                                Confirm
                            </Button>
                        </DialogFooter>
                        <div>
                            {/* AIDEV-NOTE: Negative margins must match the dialog padding
                                (p-4 mobile / p-6 desktop) or the cream bar overflows the
                                rounded edges on mobile. */}
                            <div className="-mx-4 -mb-4 flex items-center justify-end gap-2 rounded-bl-lg rounded-br-3xl border-t border-none border-brand-terra-200 bg-brand-cream-100 px-4 py-2 sm:-mx-6 sm:-mb-6 sm:px-6">
                                <Checkbox
                                    id="research-release"
                                    className="border-brand-forest-800"
                                    checked={effectiveResearchReleaseEnabled}
                                    onCheckedChange={(checked) =>
                                        toggleResearchRelease(
                                            checked as boolean,
                                        )
                                    }
                                    disabled={isResearchReleasePending}
                                />
                                <label
                                    className="text-md "
                                    htmlFor="research-release"
                                >
                                    Share this change with our team as feedback
                                </label>
                            </div>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

/**
 * A naive multi-select for the "merge" scenario.
 * We'll just do checkboxes or something simpler. But let's do a quick approach with
 * a list of parts, each is a toggle. We'll show a custom UI. For simplicity let's do
 * checkboxes in a vertical list.
 */
function MergeMultiSelect({
    otherParts,
    selectedIds,
    onChange,
}: {
    otherParts: TPartInteraction[];
    selectedIds: string[];
    onChange: (ids: string[]) => void;
}) {
    const handleToggle = (id: string) => {
        if (selectedIds.includes(id)) {
            onChange(selectedIds.filter((x) => x !== id));
        } else {
            onChange([...selectedIds, id]);
        }
    };

    if (!otherParts.length) {
        return (
            <P className="text-sm text-gray-600">No other parts available.</P>
        );
    }

    return (
        <div className="space-y-6">
            {otherParts.map((p) => (
                <label key={p.id} className="flex items-center gap-2">
                    <div>
                        <div className="mb-2 flex items-center gap-2">
                            <Checkbox
                                className="size-5 border-2"
                                checked={selectedIds.includes(p.id)}
                                onCheckedChange={() => handleToggle(p.id)}
                            />
                            <div className="text-md text-brand-terra-500">
                                {p.name}
                            </div>
                        </div>
                        <div className="text-md ">{p.description}</div>
                    </div>
                </label>
            ))}
        </div>
    );
}
